import * as React from 'react'
import { ReactElement, useState } from 'react'
import {
    Button,
    Divider,
    Link as FluentLink,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Spinner,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import { AlbumFilled, AlbumRegular, bundleIcon, DismissRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useEntity from '../../../hooks/useEntity'
import { NaturalPersonDto, PartyCardDto, PartyOverviewItemDto } from '../../../config/partyConfig'
import { PartyStatus, PartyType } from '../../../components/Form/Party/PartyForm'
import { FileStatus } from '../../../config/fileConfig'
import { composeEmailClick } from '../../../helpers/partyComposeHelper'
import { getPartyIcon } from './PartyListItem'

const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)

const useStyles = makeStyles({
    surface: {
        // With trapFocus the surface is a div[role="dialog"], so Base.scss already caps it at 92dvh
        // (94dvw on narrow screens), clips the overflow and lays it out as a flex column. All this
        // adds is the spacing between the header and the body, and a base width so the card uses the
        // room it has instead of shrink-wrapping every label onto two lines.
        rowGap: tokens.spacingVerticalS,
        minWidth: 'min(360px, 92dvw)',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
        // The header stays put while the body scrolls.
        flexShrink: 0,
    },
    headerName: {
        flexGrow: 1,
        minWidth: 0,
        // The surface sizes itself to its content, so without a cap here a very long name stretches
        // the whole card instead of being cut off.
        maxWidth: '40ch',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontWeight: tokens.fontWeightSemibold,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
        // Long lists scroll inside the card instead of growing it; minHeight bounds the flex chain,
        // without it the body keeps its content height and the scrollbar never appears.
        minHeight: 0,
        overflowY: 'auto',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
    },
    fields: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalXXS,
    },
    valueText: {
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    fileItem: {
        display: 'grid',
        gridTemplateColumns: 'minmax(70px, auto) 1fr',
        columnGap: tokens.spacingHorizontalS,
        alignItems: 'baseline',
        // Keep a stable row height so many files scroll instead of being squeezed flat.
        flexShrink: 0,
    },
    listItemText: {
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    empty: {
        color: tokens.colorNeutralForeground3,
    },
})

type FieldProps = {
    label: string
    value?: string
    /** Turns the value itself into the action, so calling and mailing need no icon of their own. */
    onClick?: () => void
}

/** A label/value row that disappears completely when the value is empty (AC5). */
function CardField(props: FieldProps): ReactElement {
    const styles = useStyles()

    if (!props.value) {
        return null
    }

    return (
        <>
            <Text>{props.label}</Text>
            {props.onClick ? (
                <FluentLink
                    className={styles.valueText}
                    onClick={event => {
                        // The overview row underneath listens for clicks as well.
                        event.stopPropagation()
                        props.onClick()
                    }}
                >
                    {props.value}
                </FluentLink>
            ) : (
                <Text className={styles.valueText}>{props.value}</Text>
            )}
        </>
    )
}

export type PartyCardPopoverProps = {
    party: PartyOverviewItemDto
    /** Lets the overview row keep its hover actions visible while the card is open (the trigger must keep its box). */
    onOpenChange?: (open: boolean) => void
}

/**
 * The read only party card on the party overview (HAM-4353). Replaces the linked-files menu: it
 * shows the contact details, the parties this party is linked to through its files (a person's
 * companies, a company's contact persons) and the files themselves, without leaving the overview.
 */
export default function PartyCardPopover(props: PartyCardPopoverProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState(false)

    const [card, , resetCard] = useEntity<PartyCardDto>(
        () => ({ url: `/party/${props.party.id}/card` }),
        open ? false : undefined,
        result => result.data,
        undefined,
        undefined,
        [open],
    )

    const party = card.data?.party
    const naturalPerson = party?.type === PartyType.Natural ? (party as NaturalPersonDto) : undefined
    // A company without a phone number or email address has nothing to show, and a heading on its own is noise.
    const hasContact = Boolean(
        naturalPerson?.gender ||
        naturalPerson?.salutation ||
        naturalPerson?.attentionOf ||
        naturalPerson?.phoneMobile ||
        party?.phoneLandline ||
        party?.email,
    )

    return (
        <Popover
            withArrow
            trapFocus
            // Anchor to the trigger and open above it (over the content, not the side panels). Without an explicit
            // positioning Fluent can fall back to the top-left corner when the trigger ref detaches.
            positioning={{ align: 'end' }}
            open={open}
            onOpenChange={(_, data) => {
                setOpen(data.open)
                props.onOpenChange?.(data.open)
                if (!data.open) {
                    resetCard()
                }
            }}
        >
            <PopoverTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={translate('party.card.open')} withArrow>
                    <Button
                        icon={<AlbumIcon />}
                        appearance="secondary"
                        size={'small'}
                        onClick={() => {
                            setOpen(true)
                            props.onOpenChange?.(true)
                        }}
                    />
                </Tooltip>
            </PopoverTrigger>

            <PopoverSurface tabIndex={-1} className={styles.surface}>
                <div className={styles.header}>
                    <div>{getPartyIcon(props.party)}</div>
                    <Text className={styles.headerName}>{props.party.displayName}</Text>
                    {props.party.status === PartyStatus.Inactive && <Text className={styles.empty}>{translate('party.status.inactive')}</Text>}
                    <Button
                        icon={<DismissRegular />}
                        appearance={'subtle'}
                        size={'small'}
                        aria-label={translate('action.close')}
                        onClick={() => {
                            setOpen(false)
                            props.onOpenChange?.(false)
                        }}
                    />
                </div>
                <Divider />

                {!card.loaded && <Spinner size={'tiny'} />}

                {card.loaded && party && (
                    <div className={styles.body}>
                        {hasContact && (
                            <div className={styles.fields}>
                                <CardField
                                    label={translate('base.gender.label')}
                                    value={naturalPerson?.gender ? translate(`base.gender.${naturalPerson.gender.toLowerCase()}`) : undefined}
                                />
                                <CardField label={translate('party.edit.person.salutation')} value={naturalPerson?.salutation} />
                                <CardField label={translate('party.edit.person.attentionOf')} value={naturalPerson?.attentionOf} />
                                <CardField
                                    label={translate('party.edit.contact.phonelandline')}
                                    value={party.phoneLandline}
                                    onClick={() => window.open(`tel:${party.phoneLandline}`)}
                                />
                                <CardField
                                    label={translate('party.edit.contact.phonemobile')}
                                    value={naturalPerson?.phoneMobile}
                                    onClick={() => window.open(`tel:${naturalPerson?.phoneMobile}`)}
                                />
                                <CardField
                                    label={translate('party.edit.contact.email')}
                                    value={party.email}
                                    onClick={() => composeEmailClick(party)()}
                                />
                            </div>
                        )}

                        <div className={styles.section}>
                            <Text weight={'semibold'}>{translate('party.card.relations')}</Text>
                            {card.data.relatedParties?.length === 0 && (
                                <Text className={styles.empty}>{translate('party.card.relations.empty')}</Text>
                            )}
                            {card.data.relatedParties?.map(relatedParty => (
                                <Text key={relatedParty.id} className={styles.listItemText}>
                                    {relatedParty.displayName}
                                </Text>
                            ))}
                        </div>

                        <div className={styles.section}>
                            <Text weight={'semibold'}>{translate('party.card.files')}</Text>
                            {card.data.files?.length === 0 && <Text className={styles.empty}>{translate('party.card.files.empty')}</Text>}
                            {card.data.files?.map(file => (
                                <Link key={file.id} to={`/file/${file.id}`} className={styles.fileItem}>
                                    <Text strikethrough={file.status === FileStatus.Archived}>{file.reference}</Text>
                                    <Text className={styles.listItemText} strikethrough={file.status === FileStatus.Archived}>
                                        {file.name}
                                    </Text>
                                </Link>
                            ))}
                        </div>

                        {party.description && (
                            <div className={styles.section}>
                                <Text weight={'semibold'}>{translate('party.card.description')}</Text>
                                <Text>{party.description}</Text>
                            </div>
                        )}
                    </div>
                )}
            </PopoverSurface>
        </Popover>
    )
}
